import type { Stats } from "node:fs";
import { lstat, readdir, realpath } from "node:fs/promises";
import { dirname, isAbsolute, join, relative, resolve } from "node:path";
import { findAiAssetsRoot } from "../../workspace-plugin/src/utils/ai-assets.js";
import type { ConnectorManager } from "@skaile/workspaces/connectors";
import {
  buildConnectorToolDefs,
  buildFlowToolDefs,
  type ConnectorToolDef,
} from "../../connectors/src/connector-prompt.js";
import { WorkspacePlugin } from "../../workspace-plugin/src/plugin.js";
import { buildWorkspaceToolDefs } from "../../workspace-plugin/src/adapters/mcp.js";
import * as z from "zod";
import type { CapabilityRegistry } from "./capability-registry.js";
import { defineCapability } from "./define-capability.js";

/** Register shared SDK-free handlers into the existing registry, never SDK server objects. */
export function registerCodexToolDefinitions(
  registry: CapabilityRegistry,
  namespace: string,
  tools: readonly ConnectorToolDef[],
): () => void {
  const owned = tools.map((tool) =>
    defineCapability({
      name: `${namespace}.${tool.name}`,
      description: tool.description,
      input: z.object(tool.inputSchema as z.ZodRawShape),
      side: "agent",
      origin: { kind: "framework" },
      audience: ["llm"],
      handler: async (input, ctx) => {
        ctx.signal?.throwIfAborted();
        return tool.handler(input);
      },
    }),
  );
  if (owned.some((cap) => registry.resolve(cap.name))) throw new Error("CODEX_TOOL_CONFLICT");
  for (const cap of owned) registry.register(cap, "agent");
  return () => {
    for (const cap of owned) if (registry.resolve(cap.name) === cap) registry.deregister(cap.name);
  };
}

function contains(root: string, target: string): boolean {
  const path = relative(root, target);
  return path === "" || (!path.startsWith("..") && !isAbsolute(path));
}

// Resolve missing future private directories through their existing ancestors too.
async function canonical(path: string): Promise<string> {
  try {
    return await realpath(path);
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
    const parent = dirname(path);
    if (parent === path) throw error;
    return resolve(await canonical(parent), relative(parent, path));
  }
}

/** Reject shared catalog/session traversals that leave their declared project scope. */
async function assertScopedTree(path: string, root: string): Promise<void> {
  let remaining = 10000;
  async function visit(entry: string): Promise<void> {
    if (--remaining < 0) throw new Error("CODEX_RESOURCE_SCOPE_INVALID");
    let stat: Stats;
    try {
      stat = await lstat(entry);
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === "ENOENT") return;
      throw error;
    }
    if (!contains(root, await realpath(entry)) || stat.isSymbolicLink())
      throw new Error("CODEX_RESOURCE_SCOPE_INVALID");
    if (stat.isDirectory())
      for (const child of await readdir(entry)) await visit(join(entry, child));
  }
  await visit(path);
}

/** Session resources share the same managers and handlers used by the Claude SDK adapter. */
export async function registerCodexResourceTools(options: {
  registry: CapabilityRegistry;
  manager: ConnectorManager | null;
  projectDir: string;
  model: string;
  privateDirectory: string;
}): Promise<() => Promise<void>> {
  const project = await realpath(options.projectDir);
  const privateRoot = await canonical(resolve(options.privateDirectory));
  if (contains(project, privateRoot) || contains(privateRoot, project))
    throw new Error("CODEX_RESOURCE_SCOPE_INVALID");
  const statePath = join(project, ".skaile", "plugin-state.json");
  const assertStateScope = async () => {
    if (!contains(project, await canonical(statePath)))
      throw new Error("CODEX_RESOURCE_SCOPE_INVALID");
  };
  await assertStateScope();
  const plugin = new WorkspacePlugin({
    projectDir: options.projectDir,
    connectorManager: options.manager ?? undefined,
    driver: "codex",
    provider: "openai",
    model: options.model,
  });
  await plugin.store.load();
  const removals: Array<() => void> = [];
  const protectFlow = (tools: ConnectorToolDef[]) =>
    tools.map((tool) => ({
      ...tool,
      handler: async (input: Record<string, unknown>) => {
        if (tool.name === "mount_read") {
          const mount = options.manager
            ?.listConnectors()
            .find((entry) => entry.id === input.mount_id);
          if (!mount?.mountPath || typeof input.path !== "string")
            throw new Error("CODEX_RESOURCE_SCOPE_INVALID");
          const root = await realpath(mount.mountPath);
          const target = await realpath(resolve(root, input.path));
          if (!contains(project, root) || !contains(root, target))
            throw new Error("CODEX_RESOURCE_SCOPE_INVALID");
        }
        if (
          tool.name.startsWith("skill_") ||
          ["flow_list", "flow_show", "flow_find_flow_for_session"].includes(tool.name)
        ) {
          const assets = await findAiAssetsRoot(project);
          if (assets) await assertScopedTree(assets, project);
        }
        if (tool.name.startsWith("session_") || tool.name.includes("session"))
          await assertScopedTree(join(project, ".skaile", "sessions"), project);

        if (
          tool.name === "connector_exec" &&
          options.manager &&
          typeof input.connector_id === "string" &&
          options.manager
            .listConnectors()
            .some((entry) => entry.id === input.connector_id && entry.driver === "flow") &&
          !buildFlowToolDefs(options.manager, input.connector_id).some(
            (operation) => operation.name === input.operation,
          )
        )
          throw new Error("CODEX_TOOL_UNAVAILABLE");
        return tool.handler(input);
      },
    }));
  try {
    // Secret diagnostics are trusted-runtime operations, not model tools.
    removals.push(
      registerCodexToolDefinitions(
        options.registry,
        "workspace",
        protectFlow(
          buildWorkspaceToolDefs(plugin).filter(
            (tool) =>
              ![
                "workspace_test_secret",
                "workspace_validate",
                "workspace_reload",
                "skill_check_requirements",
              ].includes(tool.name),
          ),
        ),
      ),
    );
    if (options.manager)
      removals.push(
        registerCodexToolDefinitions(
          options.registry,
          "connector",
          protectFlow(buildConnectorToolDefs(options.manager, { shellAccess: false })),
        ),
      );
  } catch (error) {
    for (const remove of removals) remove();
    throw error;
  }
  return async () => {
    for (const remove of removals) remove();
    await assertStateScope();
    await plugin.shutdown();
  };
}

/** A flow exposes only its existing agent operations; the host alone can grant approvals. */
export function registerCodexFlowTools(
  registry: CapabilityRegistry,
  manager: ConnectorManager,
  connectorId: string,
): () => void {
  const tools = buildFlowToolDefs(manager, connectorId);
  if (!tools.length) throw new Error("CODEX_REQUIRED_TOOL_UNAVAILABLE");
  return registerCodexToolDefinitions(registry, "flow", tools);
}
